import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@rumbledirect/ui-v2/cn";

interface PanelProps {
  /** Optional leading icon; chart cards pass none and get a plain title header. */
  readonly icon?: LucideIcon;
  readonly title: string;
  /** Optional count pill shown at the end of the header row. */
  readonly count?: number;
  /**
   * Optional header-trailing content (e.g. an "Alles bekijken" link), shown
   * after the count pill if both are present.
   */
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * Dashboard panel wrapper: a title header (with an optional leading icon and
 * an optional count pill), matching the rounded-2xl/border/bg-surface/shadow-xs
 * card language used elsewhere in this app (see `components/ui/Card.tsx`/
 * `StatCard.tsx`). Feature-local to `src/dashboard/` rather than
 * `components/ui/` per an explicit decision: it is currently single-use (only
 * the Werkverdeling panel), so it is not yet a design-system primitive.
 */
export function Panel({
  icon: Icon,
  title,
  count,
  action,
  children,
  className,
}: PanelProps) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-border/70 bg-surface p-5 shadow-xs",
        className,
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        {Icon && (
          <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
        )}
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {count !== undefined && (
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
        {action && (
          <span className={count === undefined ? "ml-auto" : ""}>{action}</span>
        )}
      </div>
      {children}
    </section>
  );
}
